// ProductImageGeneratorFrontendOnly.tsx
import React, { useState, useRef, useEffect } from 'react';
import './ImageAI.scss';
import { closeIcon, sparklesIcon, arrowDownIcon } from '../../utils/images';
import { getTags, generateProductImage, addProduct } from '../../services/productsList';
import { Tag, TagResponse } from '../../types/Product';
import { useProducts } from '../../contexts/ProductsContext';
import { blobUrlToFile } from '../../utils/imageUtils';

interface ImageAIProps {
  onClose: () => void;
}

export const ImageAI: React.FC<ImageAIProps> = ({ onClose }) => {
  const [productName, setProductName] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<TagResponse[]>([]);
  const [availableTags, setAvailableTags] = useState<TagResponse[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [baseImage, setBaseImage] = useState<string | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tagsLoading, setTagsLoading] = useState<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  
  const { refreshProducts } = useProducts();

  // Fetch available tags on component mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        setTagsLoading(true);
        const tags = await getTags();
        setAvailableTags(tags);
      } catch (error) {
        console.error('Error fetching tags:', error);
        setError('Failed to load tags');
      } finally {
        setTagsLoading(false);
      }
    };

    fetchTags();
  }, []);

  // Clear form function
  const clearForm = () => {
    setProductName('');
    setSelectedTags([]);
    setBaseImage(null);
    setGeneratedImage(null);
    setUploadedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Validate form function
  const validateForm = (): string | null => {
    if (!productName.trim()) {
      return 'Product name is required';
    }
    if (!uploadedFile || !baseImage) {
      return 'Base image is required';
    }
    if (!generatedImage) {
      return 'Please generate an AI image first';
    }
    if (selectedTags.length === 0) {
      return 'Please select at least one tag';
    }
    return null;
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file); // Store the actual file
      const reader = new FileReader();
      reader.onload = () => {
        setBaseImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Toggle tag selection
  const toggleTagSelection = (tag: Tag) => {
    setSelectedTags(prev => {
      const isSelected = prev.some(t => t.id === tag.id);
      if (isSelected) {
        return prev.filter(t => t.id !== tag.id);
      } else {
        return [...prev, tag];
      }
    });
  };

  // Remove a selected tag
  const removeTag = (tagId: number) => {
    setSelectedTags(prev => prev.filter(tag => tag.id !== tagId));
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Generate image using AI
  const generateImage = async () => {
    if (!uploadedFile) {
      setError('Please upload a reference image');
      return;
    }

    if (selectedTags.length === 0) {
      setError('Please select at least one tag');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);

    try {
      // Convert selected tags to comma-separated string
      const tagsString = selectedTags.map(tag => tag.name).join(',');

      console.log(tagsString);
      
      // Call the API
      const result = await generateProductImage(uploadedFile, tagsString);
      
      // Handle blob URL image response
      setGeneratedImage(result.ai_image_url);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while generating the image');
      console.error('Error generating image:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  // Create product function
  const createProduct = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Convert blob URL back to File for AI image
      const aiImageFile = await blobUrlToFile(generatedImage!, `${productName}_ai_generated.png`);
      
      // Prepare data for API
      const baseImages = [uploadedFile!]; // Send as array
      const aiImages = [aiImageFile]; // Send as array
      const tagIds = selectedTags.map(tag => tag.id).join(','); // Tag IDs as comma-separated string

      // Call API to create product
      const result = await addProduct(baseImages, aiImages, tagIds, productName);
      
      console.log('Product created successfully:', result);
      
      // Clear form after successful creation
      clearForm();
      
      // Refresh products list in context
      await refreshProducts();
      
      // Close popup
      onClose();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while creating the product');
      console.error('Error creating product:', err);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    clearForm();
    onClose();
  };

  return (
    <div className="product-popup">
      <div className="product-popup__header">
        <div className="product-popup__close" onClick={handleCancel}>
          <img className="product-popup__close--icon" src={closeIcon} alt="close" />
        </div>
        <h1 className="product-popup__title">Add Product</h1>
        <div className="product-popup__actions">
          <button 
            className="product-popup__cancel-button"
            onClick={handleCancel}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button 
            className="product-popup__confirm-button"
            onClick={createProduct}
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Confirm'}
          </button>
        </div>
      </div>

      <div className="product-popup__form-grid">
        <div className="product-popup__form-column">
          <div className="product-popup__form-wrapper">
            <div className="product-popup__form-field">
              <label className="product-popup__label product-popup__label--first">Product Name</label>
              <input
                type="text"
                className="product-popup__input"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Input"
                disabled={isCreating}
              />
            </div>
          </div>

          <div className="product-popup__hashtag-section">
                        {/* Tags dropdown */}
                        <div className="product-popup__dropdown-container">
              <div 
                className="product-popup__dropdown" 
                onClick={() => !isCreating && setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className="product-popup__dropdown--text">
                  {tagsLoading ? 'Loading tags...' : 'Select tags'}
                </span>
                <img 
                  className={`product-popup__dropdown--icon ${isDropdownOpen ? 'open' : ''}`}
                  src={arrowDownIcon} 
                  alt="dropdown icon" 
                />
              </div>
              
              {isDropdownOpen && !tagsLoading && !isCreating && (
                <ul className="product-popup__dropdown-menu">
                  {availableTags.map(tag => (
                    <li 
                      key={tag.id} 
                      className={`product-popup__dropdown-item ${
                        selectedTags.some(t => t.id === tag.id) ? 'selected' : ''
                      }`}
                      onClick={() => toggleTagSelection(tag)}
                    >
                      <span>{tag.name}</span>
                      {selectedTags.some(t => t.id === tag.id) && <span className="checkmark">✓</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            
            {/* Selected tags display */}
            <div className="product-popup__hashtag-container">
              {selectedTags.map(tag => (
                <div key={tag.id} className="product-popup__hashtag">
                  <span>#{tag.name}</span>
                  <button 
                    className="product-popup__hashtag--remove"
                    onClick={() => removeTag(tag.id)}
                    disabled={isCreating}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="product-popup__image-grid">
        <div className="product-popup__image-column">
          <div className="product-popup__upload-area">
            <input 
              type="file" 
              ref={fileInputRef}
              className="product-popup__file-input" 
              accept="image/*" 
              onChange={handleFileUpload} 
              style={{ display: 'none' }}
              disabled={isCreating}
            />
            {baseImage ? (
              <img 
                src={baseImage} 
                alt="Base product" 
                className="product-popup__image product-popup__preview-image" 
              />
            ) : (
              <div className="product-popup__image product-popup__upload-placeholder">
                <input 
                  type="text"
                  className="product-popup__image--input"
                  placeholder="Upload"
                  value={baseImage || ""}
                  onChange={(e) => setBaseImage(e.target.value)}
                  disabled={isCreating}
                />
              </div>
            )}
            <p className="product-popup__image-caption">Base Product Image</p>
            <button 
              className="product-popup__upload-button"
              onClick={triggerFileInput}
              type="button"
              disabled={isCreating}
            >
              {baseImage ? 'Change Image' : 'Upload Image'}
            </button>
          </div>
        </div>

        <div className="product-popup__generate-button-container">
          <button
            className="product-popup__generate-button"
            onClick={generateImage}
            disabled={!baseImage || selectedTags.length === 0 || isGenerating || isCreating}
          >
            <img src={sparklesIcon} alt="sparkles" className="product-popup__generate-icon" />
            {isGenerating ? 'Generating...' : 'AI Generate'}
          </button>
        </div>

        <div className="product-popup__image-column">
          {generatedImage ? (
            <div className="product-popup__generated-image-container product-popup__image">
              <img 
                src={generatedImage} 
                alt="AI generated" 
                className="product-popup__generated-image" 
              />
            </div>
          ) : (
            <>
            <div className="product-popup__image product-popup__upload-placeholder">
            </div>
            <div className="product-popup__generate-container">
              {isGenerating && (
                <div className="product-popup__loading">
                  <div className="product-popup__spinner"></div>
                  <span>Generating...</span>
                </div>
              )}
            </div>
            </>
          )}
          <p className="product-popup__image-caption">AI Generate Image</p>
          {error && <p className="product-popup__error">{error}</p>}
        </div>
      </div>
    </div>
  );
};